"use client";

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ADSENSE_CLIENT, ADSENSE_SLOTS, ADSENSE_USE_MOCK } from "@/lib/adsense";
import { cn } from "@/lib/utils";
import { useAdSensePush } from "@/hooks/useAdSensePush";

const CLOSE_DELAY_MS = 5_000;
const CLOSE_DELAY_SEC = Math.ceil(CLOSE_DELAY_MS / 1000);

interface AdSenseInterstitialProps {
  open: boolean;
  onClose: () => void;
}

export default function AdSenseInterstitial({
  open,
  onClose,
}: AdSenseInterstitialProps) {
  const [canClose, setCanClose] = useState(false);
  const [remainingSec, setRemainingSec] = useState(CLOSE_DELAY_SEC);
  // dev/목업 모드에는 인터스티셜 슬롯 env가 없을 수 있으므로(.env.local 미설정)
  // placeholder 슬롯으로 대체해 mock SDK가 채울 수 있게 한다. 프로덕션은 실제 슬롯 필요.
  const slot = ADSENSE_SLOTS.interstitial ?? (ADSENSE_USE_MOCK ? "mock-interstitial" : undefined);
  const insRef = useRef<HTMLModElement>(null);

  // 모달 안의 responsive ins는 마운트/애니메이션 중 width=0인 시점이 있어,
  // 그때 push되면 availableWidth=0으로 미충전된다(데스크톱 빈 화면·모바일 미노출).
  // 훅이 폭이 확정될 때까지 rAF로 대기한 뒤 push한다(JCG-2057).
  useAdSensePush(insRef, open && !!slot, open);

  // open 변경 시 카운트다운 reset + interval 설정 — 외부 타이머 동기화
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCanClose(false);
      setRemainingSec(CLOSE_DELAY_SEC);
      return;
    }

    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remain = Math.max(0, Math.ceil((CLOSE_DELAY_MS - elapsed) / 1000));
      setRemainingSec(remain);
      if (elapsed >= CLOSE_DELAY_MS) {
        setCanClose(true);
        window.clearInterval(tick);
      }
    }, 250);

    return () => window.clearInterval(tick);
  }, [open]);

  if (!open) return null;
  if (!slot) return null;

  const handleOpenChange = (next: boolean) => {
    if (!next && canClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-md sm:max-w-lg p-0 gap-0 overflow-hidden"
        onEscapeKeyDown={(e) => {
          if (!canClose) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (!canClose) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (!canClose) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">광고</DialogTitle>
        <DialogDescription className="sr-only">
          {CLOSE_DELAY_SEC}초 후 광고를 닫을 수 있습니다.
        </DialogDescription>

        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/40">
          <span className="text-xs text-muted-foreground">광고 (Sponsored)</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!canClose}
            onClick={() => canClose && onClose()}
            className={cn(
              "h-7 px-2 text-xs",
              !canClose && "opacity-60 cursor-not-allowed",
            )}
          >
            {canClose ? "닫기" : `${remainingSec}초 후 닫기`}
          </Button>
        </div>

        <div className="p-4 flex justify-center items-center min-h-[280px] bg-background">
          <ins
            ref={insRef}
            className="adsbygoogle block w-full"
            style={{ display: "block", minHeight: 280 }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
